import { 
  Title, 
  Text, 
  Button, 
  Stack,
  Box,
  Image,
  Container,
  Group,
  Anchor,
  Paper,
  List,
  ThemeIcon,
  rem,
  Flex,
  Divider
} from '@mantine/core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * About page component explaining how the LinkedIn post tester works
 * and information about RapidLab
 */
const About = () => {
  const navigate = useNavigate();
  const [hoverButton, setHoverButton] = useState(false);
  
  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background graphic elements */}
      <Box
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          top: '-20%',
          right: '-10%',
          background: 'radial-gradient(circle, rgba(10, 102, 194, 0.15) 0%, rgba(10, 102, 194, 0.05) 50%, transparent 70%)',
          borderRadius: '50%',
          opacity: 0.5,
          zIndex: 0,
        }}
      />
      <Box 
        style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          bottom: '-10%',
          left: '-15%',
          background: 'radial-gradient(circle, rgba(10, 102, 194, 0.15) 0%, rgba(10, 102, 194, 0.05) 50%, transparent 70%)',
          borderRadius: '50%',
          opacity: 0.5,
          zIndex: 0,
        }}
      />
      
      {/* Header - Updated to match RapidLab clean style */}
      <Box
        component="header"
        style={{
          height: 80, 
          background: 'white', 
          display: 'flex',
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          borderBottom: '1px solid rgba(10, 102, 194, 0.1)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
          width: '100%'
        }}
      >
        <Container size="lg" style={{ 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0 1rem',
          width: '100%',
          maxWidth: '1200px'
        }}>
          <Flex align="center" gap={rem(8)} style={{ marginRight: 'auto' }}>
            <Box 
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => window.location.href = '/'}
            >
              {/* SVG Lightning bolt icon */}
              <Box style={{ 
                marginRight: rem(8), 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                order: 1
              }}>
                <svg 
                  width="30" 
                  height="30" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ color: '#0A66C2' }}
                >
                  <path 
                    d="M13 10V3L4 14H11V21L20 10H13Z" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
              <Text style={{ 
                color: '#0A66C2', 
                fontWeight: 700, 
                fontSize: '1rem',
                letterSpacing: '-0.01em',
                order: 2,
                fontFamily: '"Bio Sans", sans-serif'
              }}>
                RAPIDLAB
              </Text>
            </Box>
          </Flex>
          
          <Flex gap={rem(16)} justify="flex-end" style={{ marginLeft: 'auto' }}>
            <Anchor 
              href="#/about"
              style={{ 
                color: '#0A66C2',
                textDecoration: 'none',
                fontWeight: 700, // Make current page bold
                fontSize: '1rem',
                fontFamily: '"Bio Sans", sans-serif',
                cursor: 'pointer'
              }}
              styles={{
                root: {
                  '&:hover': {
                    color: '#084785',
                  }
                }
              }}
            >
              About
            </Anchor>
            <Anchor 
              href="https://calendly.com/mat-rapid-lab/30min"
              target="_blank"
              style={{ 
                color: '#0A66C2',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                fontFamily: '"Bio Sans", sans-serif'
              }}
              styles={{
                root: {
                  '&:hover': {
                    color: '#084785',
                  }
                }
              }}
            >
              Contact
            </Anchor>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Box 
        style={{ 
          flex: '1',
          position: 'relative',
          padding: `${rem(48)} ${rem(20)}`,
          marginTop: 80, // Add margin to account for fixed header
        }}
      >
        <Container size="lg">
          <Title order={1} style={{ 
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 800,
            lineHeight: 1.2,
            color: '#0A66C2',
            marginBottom: rem(32)
          }}>
            About LinkedIn Post Tester
          </Title>
          
          <Stack gap={rem(32)}>
            <Paper 
              shadow="sm" 
              p="lg" 
              radius="md" 
              style={{ 
                border: '1px solid rgba(10, 102, 194, 0.1)',
                background: 'white'
              }}
            >
              <Title order={3} style={{ color: '#0A66C2', marginBottom: rem(16) }}>
                How It Works
              </Title>
              <Text style={{ lineHeight: 1.6, fontSize: '1.05rem' }}>
                Our LinkedIn Post Tester uses AI to simulate how different audience personas might react to your content. 
                It's a synthetic testing tool that analyzes your post and predicts engagement based on various factors like 
                content quality, tone, and relevance to your target audience.
              </Text>
              <Divider my={rem(16)} variant="dashed" color="rgba(10, 102, 194, 0.2)" />
              <Text style={{ lineHeight: 1.6, fontSize: '1.05rem' }}>
                While the results are often remarkably accurate, remember to take everything with a pinch of salt.
                This is an experimental tool built for fun, but the feedback can be incredibly helpful for improving
                your LinkedIn content strategy.
              </Text>
            </Paper>

            <Paper 
              shadow="sm" 
              p="lg" 
              radius="md" 
              style={{ 
                border: '1px solid rgba(10, 102, 194, 0.1)',
                background: 'white'
              }}
            >
              <Title order={3} style={{ color: '#0A66C2', marginBottom: rem(16) }}>
                About RapidLab
              </Title>
              <Text style={{ lineHeight: 1.6, fontSize: '1.05rem', marginBottom: rem(16) }}>
                RapidLab is a team of innovators who specialize in transforming ideas into reality through rapid 
                prototyping and development. We believe in the power of quick iterations and testing to create 
                products that truly resonate with users.
              </Text>
              <Text style={{ lineHeight: 1.6, fontSize: '1.05rem' }}>
                This LinkedIn Post Tester is one of our experimental projects, built to explore how AI can help creators
                craft better content. We're passionate about building tools that help people work smarter and achieve better results.
              </Text>
            </Paper>

            <Paper 
              shadow="sm" 
              p="lg" 
              radius="md" 
              style={{ 
                border: '1px solid rgba(10, 102, 194, 0.1)',
                background: 'white'
              }}
            >
              <Title order={3} style={{ color: '#0A66C2', marginBottom: rem(16) }}>
                Get In Touch
              </Title>
              <Text style={{ lineHeight: 1.6, fontSize: '1.05rem', marginBottom: rem(16) }}>
                Want to learn more about synthetic testing and how it can be applied to your products or content?
                We'd love to chat! Whether you're interested in custom tools, consulting, or just want to say hello,
                don't hesitate to reach out.
              </Text>
              
              <Box style={{ position: 'relative', display: 'inline-block' }}>
                <Button 
                  component="a"
                  href="https://calendly.com/mat-rapid-lab/30min"
                  target="_blank"
                  size="lg"
                  style={{
                    fontSize: '1.1rem',
                    padding: `${rem(10)} ${rem(24)}`,
                    paddingRight: rem(40),
                    height: 'auto',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #2684FF 0%, #0073E6 100%)',
                    borderRadius: rem(8),
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                  }}
                  styles={{
                    root: {
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                        background: 'linear-gradient(135deg, #3393ff 0%, #2684FF 100%)',
                      }
                    }
                  }}
                  onMouseEnter={() => setHoverButton(true)}
                  onMouseLeave={() => setHoverButton(false)}
                >
                  Contact Us
                  <Box
                    component="span"
                    style={{
                      position: 'absolute',
                      right: rem(20),
                      display: 'inline-block',
                      transition: 'transform 0.3s ease',
                      transform: hoverButton ? 'translateX(5px)' : 'none',
                      color: 'white',
                      fontSize: '1.1rem',
                    }}
                  >
                    →
                  </Box>
                </Button>
              </Box>
            </Paper>
          </Stack>
        </Container>
      </Box>
      
      {/* Footer */}
      <Box
        component="footer"
        style={{
          width: '100%', 
          borderTop: '1px solid rgba(10, 102, 194, 0.15)',
          background: '#f9f9f9',
          padding: `${rem(12)} 0`,
        }}
      >
        <Container size="lg">
          <Flex direction="column" align="center" gap={rem(8)}>
            <Text style={{ 
              fontSize: '0.8rem',
              color: '#555',
            }}>
              Transforming ideas into reality through rapid prototyping.
            </Text>
            
            <Flex justify="center" gap={rem(16)}>
              <Anchor 
                href="https://test.rapid-lab.com/privacy" 
                target="_blank"
                style={{
                  color: '#0A66C2',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  padding: '0 8px',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      color: '#084785',
                    }
                  }
                }}
              >
                Privacy Policy
              </Anchor>
              <Text style={{ color: '#999', fontSize: '0.75rem' }}>•</Text>
              <Anchor 
                href="https://test.rapid-lab.com/terms" 
                target="_blank"
                style={{
                  color: '#0A66C2',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  padding: '0 8px',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      color: '#084785',
                    }
                  }
                }}
              >
                Terms of Service
              </Anchor>
            </Flex>
            
            <Text style={{ 
              fontSize: '0.7rem',
              color: '#999',
            }}>
              © 2025 RapidLab
            </Text>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};

export default About; 